import * as Sentry from '@sentry/browser';
import { getLoggedIn } from '@hotjar/webapp/domains/common/user-data';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { getCurrentUser } from '@hotjar/user';
import {
    getUserResources,
    USER_RESOURCES_QUERY_KEY,
    setCurrentSiteInLocalStorage,
    userResourcesModelToDto
} from '@hotjar/user-resources';
import { mapSiteDtoToModel, mapSiteModelToDto, nonReactSetPreferredCurrentSiteId } from '@hotjar/site';
import { mapOwnedAccountModelToDto } from '@hotjar/account';
import {
    bus,
    trackingCodeInstalled,
    siteUrlUpdated,
    currentSiteChanged,
    refreshData,
    currentSiteSet
} from '@hotjar/webapp/utils/events';
import { CURRENT_USER_QUERY_KEY } from '@hotjar/user';
import { DAILY_SESSION_QUERY_KEY } from '@hotjar/webapp/hooks/useDailySession';
import { getDailySessions } from '@hotjar/site';
import { PlanRepository } from '@hotjar/webapp/domains/revenue/plan/plan-repository';
import { setSentryIdentity, setSentrySiteIdTag } from '@hotjar/webapp/tracking/sentry';
import { loadUserDataFromStorage } from '@hotjar/webapp/domains/common/user-data/index';

/**
 * See packages/legacy/src/webapp/domains/common/user/model.ts
 * @typedef {Object} CurrentUser
 */

/**
 * Provides access to user data stored in LocalStorage.
 */
angular
    .module('hjApp.user')
    .service('UserData', function ($q, $rootScope, Loader, LocalStorage, Notify, restDefaultHandlers, Segment, $state) {
        var resourcesPromise,
            refreshQueue = [], // Service lock to stop
            self = this,
            userData,
            userResources;

        /*******************
         * PUBLIC FUNCTIONS
         *****************/

        /**
         * Get a specific item from the user data.
         * @param name Key for item to get
         */
        this.get = function (name) {
            return userData[name];
        };

        /**
         * Get all the user data available;
         */
        this.getAll = function () {
            return userData;
        };

        this.isDemoAccount = () => this.get('isDemoUser');

        this.refreshResourcesFromServer = function refreshResourcesFromServer() {
            return self.fetchResourcesFromServer().then(
                function (resources) {
                    userResources = resources;

                    var currentSite = userData.currentSite;
                    var sites = userResources.sites;

                    // Either use the updated object for the current site with the
                    // same id.
                    //
                    // Else, if this does not exist (e.g. has been deleted or the
                    // user no longer has access to it), set to `undefined` if the
                    // sites list is empty, otherwise use the first site in the list.
                    var updatedCurrentSite = currentSite ? _.find(sites, { id: currentSite.id }) : undefined;
                    if (updatedCurrentSite) {
                        self.currentSite = updatedCurrentSite;
                    } else {
                        if (
                            window.location.pathname !== '/onboarding' &&
                            $state.current.name !== 'settings.account' &&
                            $state.current.name !== ''
                        ) {
                            $rootScope.$broadcast('permissionRequired', $state.params);
                            self.currentSite = undefined;
                        } else {
                            self.currentSite = _.isEmpty(sites) ? undefined : sites[0];
                        }
                    }

                    nonReactSetPreferredCurrentSiteId(self.currentSite?.id);
                    if (self.currentSite) {
                        setCurrentSiteInLocalStorage(mapSiteDtoToModel(self.currentSite, resources.organizations));
                    } else {
                        setCurrentSiteInLocalStorage(undefined);
                    }

                    userResources.sites.forEach((site) => (site.name = site.name || self.siteNamePlaceholder));

                    $rootScope.$broadcast('userDataRefreshed');

                    return userResources;
                },
                function (err) {
                    restDefaultHandlers.onError(err);
                    return $q.reject(err);
                }
            );
        };

        this.fetchResourcesFromServer = function fetchResourcesFromServer() {
            var userId = self.get('userId');

            if (!userId) {
                return refreshFromServer();
            }

            // If we're already getting the resources then return the promise to be chained.
            if (resourcesPromise) {
                return resourcesPromise;
            }

            queryClient.invalidateQueries(USER_RESOURCES_QUERY_KEY);
            resourcesPromise = queryClient
                .fetchQuery(USER_RESOURCES_QUERY_KEY, () => getUserResources(userId), {
                    staleTime: 0,
                    cacheTime: 60 * 60 * 1000
                })
                .then((response) => {
                    return userResourcesModelToDto(response);
                })
                .then(
                    function (response) {
                        var organizations = response.organizations;

                        _.each(organizations, function (organization) {
                            organization.sites = _(response.sites)
                                .filter({ organization_id: organization.id })
                                .map(function (site) {
                                    site.organization = _.pick(organization, [
                                        'id',
                                        'name',
                                        'plan',
                                        'size',
                                        'sample_rate',
                                        'always_on_rate',
                                        'effective_plan',
                                        'account_id',
                                        'created',
                                        'features',
                                        'price_type',
                                        'next_price_type',
                                        'price',
                                        'access',
                                        'users'
                                    ]);
                                    return site;
                                })
                                .value();
                        });

                        var sites = _(organizations).map('sites').flatten().value();

                        $rootScope.$digest();

                        return {
                            sites: sites,
                            organizations: organizations
                        };
                    },
                    function (err) {
                        return $q.reject(err);
                    }
                )
                .finally(function () {
                    resourcesPromise = null;
                });

            return resourcesPromise;
        };

        /**
         * Get all the sites for the user.
         * @returns {Promise.<Site[]>} Promise resolving to the site list.
         */
        this.getSites = function getSites() {
            return self.getResources().then(
                function (resources) {
                    return resources.sites;
                },
                function (err) {
                    restDefaultHandlers.onError(err);
                    return $q.reject(err);
                }
            );
        };

        /**
         * Get all the organizations for the user.
         * @returns {Promise.<Organization[]>} Promise resolving to the organization list.
         */
        this.getOrganizations = function getOrganizations() {
            return self.getResources().then(function (resources) {
                return resources.organizations;
            });
        };

        /**
         * Get the resources for the user. These include sites and organizations.
         *
         * @returns {Promise} Promise resolving to an object containing sites and organizations.
         */
        this.getResources = function getResources(refetch = false) {
            if (refetch) userResources = undefined;

            if (!userResources) {
                return self.refreshResourcesFromServer();
            }
            return $q.when(userResources);
        };

        /**
         * Set a specific item value in the user data and persist to local storage.
         *
         * @param nameOrMap Key for item to set OR a map of key value pairs.
         * @param value Value to set OR undefined
         */
        this.set = function (nameOrMap, value) {
            if (_.isObject(nameOrMap)) {
                _.extend(userData, nameOrMap, function (objValue, srcValue) {
                    return srcValue;
                });
            } else {
                userData[nameOrMap] = value;
            }
            saveUserData();
        };

        /**
         * Set the userdata based on a login or 'userdata' response from the server.
         *
         * ToDo: Potential future removals (been replace in account objects):
         *  - currency: still used in a lot of places. An option is to make currency part of the account.
         *  - hasPaymentDetails: still used in a lot of places. Can only remove this once we better combine
         *                       the two pricing versions with one another.
         *  - recurringPaymentFailed: needed for the v1 billing page. Using the subscription service is possible but
         *                            the property doesn't update unless there is a refresh.
         * @param response Server response.
         */
        this.setFromServerResponse = function (response) {
            this.set({
                ...response,
                // getUserResources returns an account model,
                // we change this to dto since this what is expected across angular ctrl's & services
                accounts: response.accounts?.map((account) => mapOwnedAccountModelToDto(account)),
                accountId: response.accounts && response.accounts[0].id,
                countryCode: response.country,
                version: window.settings.version
            });
        };

        /**
         * Save. This is specifically used when .set() cannot be used.
         */
        this.save = function () {
            saveUserData();
        };

        /**
         * Remove all user data from local storage.
         */
        this.removeAll = function () {
            _.each(userData, function (value, key) {
                delete userData[key];
            });

            userResources = undefined;
            setCurrentSiteInLocalStorage(undefined);
            saveUserData();
        };

        /**
         * Re-gets and sets UserData from the server.
         * NOTE: This is a heavy operation to avoid unless required.
         */
        this.refreshFromServer = function (callback) {
            if (refreshQueue.push(callback) === 1) {
                // If we're adding the first one in, start the queue.
                refreshFromServer();
            }
        };

        /**
         * This function is used during the sign-up process. There is a possibility that the user (call them U1)
         * was once logged-in and the user data was set to local storage.
         * If they log out and try to create a new user (call them U2) via /register form,
         * they should be able to navigate to insights after succesfully completing step 2 (password).
         * If we don't clear localStorage on register page the U2 will be redirected to the
         * site saved to local storage by U1, causing an error.
         */
        this.clearUserData = function () {
            userData = {};
            saveUserData();
        };

        /**
         * Get daily sessions value and calculate coverage percentage
         */
        this.getCoveragePercentage = function () {
            return queryClient
                .fetchQuery(`${DAILY_SESSION_QUERY_KEY}-${userData.currentSite.id}`, () =>
                    getDailySessions(userData.currentSite.id)
                )
                .then((response) => {
                    const { available_plans = [] } = window.settings ?? {};
                    const planRepository = new PlanRepository(available_plans);
                    const ccDailyAllowance = userData.currentSite?.effective_plan_id
                        ? planRepository.byId(userData.currentSite.effective_plan_id).ccDailyAllowance
                        : 0;

                    return Math.min(100, Math.floor((ccDailyAllowance / response.sessions) * 100)) || 0;
                })
                .catch((e) =>
                    Sentry.captureMessage(`Could not get the daily sessions from site ${userData.currentSite.id}. ${e}`)
                );
        };

        /*******************
         * PROPERTY DEFINITIONS
         *****************/

        /**
         * Return true if the user is logged in, false otherwise.
         */
        Object.defineProperty(this, 'loggedIn', {
            get: getLoggedIn,
            configurable: true
        });

        /**
         * Return the current site the user is managing.
         */
        Object.defineProperty(this, 'currentSite', {
            get: function () {
                return userData.currentSite;
            },
            set: function (site) {
                userData.currentSite = site;
                saveUserData();
                if (site) {
                    setSentrySiteIdTag(site.id);
                }
            }
        });

        /**
         * Return the accounts object.
         */
        Object.defineProperty(this, 'accounts', {
            get: function () {
                return userData.accounts;
            }
        });

        /**
         * DO NOT USE THIS PROPERTY
         *
         * This is a work-around for a deep non-async stack where it would be
         * too difficult to refactor to work with the user resources correctly.
         *
         * Consider this property volatile and dangerous.
         *
         * For reference, this is used by the Cost.getPaidSites function ONLY
         *
         * - rob.lester & chiara
         */
        Object.defineProperty(this, 'userResources', {
            get: function () {
                return userResources;
            }
        });

        /**
         * Return true if resources are loaded.
         */
        Object.defineProperty(this, 'areResourcesLoaded', {
            get: function () {
                return !!userResources;
            }
        });

        Object.defineProperty(this, 'siteNamePlaceholder', {
            get: function () {
                return 'My site';
            }
        });

        /*******************
         * PRIVATE FUNCTIONS
         *****************/
        /**
         * Serialize the user data to JSON and store it in LocalStorage.
         */
        function saveUserData() {
            LocalStorage.set('userData', userData);
            nonReactSetPreferredCurrentSiteId(userData?.currentSite?.id);
        }

        function identify(userData) {
            // Since by default the userData is empty, only identify if we have
            // a userId
            if (userData.userId) {
                Segment.identify(userData);
            }
        }

        // The react api call maps these user prefixed files to more usable names so we remap them
        // back to not break the angular code.
        /**
         * @param {CurrentUser} user
         */
        function renameUserProperties(user) {
            return {
                ...user,
                userCreated: user.created,
                userEmail: user.email,
                userEmailBlocked: user.emailBlocked,
                userId: user.id,
                userName: user.name,
                userPictureUrl: user.avatar,
                userRole: user.role
            };
        }

        /**
         * Refresh UserData by requesting it from the server and updating it locally.
         */
        function refreshFromServer() {
            var callback = refreshQueue[0];

            // We use queryClient from react-query so that the request is cached for use by the react hook
            // useCurrentUser
            return queryClient
                .fetchQuery(CURRENT_USER_QUERY_KEY, getCurrentUser, { staleTime: 0, cacheTime: 60 * 60 * 1000 })
                .then(
                    /**
                     * @param {?CurrentUser} user
                     */
                    function (user) {
                        if (!user) {
                            $rootScope.$digest();
                            $rootScope.$emit('unauthorized');
                            return;
                        }

                        const mappedUser = renameUserProperties(user);
                        self.setFromServerResponse(mappedUser);
                        Segment.setAnonymizedUserId(self.get('currentIp'));
                        Segment.setCountryCode(self.get('countryCode'));
                        setSentryIdentity(mappedUser.userId);
                        identify(self.getAll());

                        $rootScope.$digest();
                        return self.refreshResourcesFromServer();
                    },
                    function (error) {
                        $rootScope.$digest();
                        restDefaultHandlers.onError(error.response);
                    }
                )
                .finally(function (response) {
                    if (_.isFunction(callback)) {
                        var resp = callback(userData);
                        if (resp && _.isFunction(resp.then)) {
                            resp.then(function () {
                                processQueue();
                            });
                        } else {
                            processQueue();
                        }
                    } else {
                        processQueue();
                    }

                    return response;
                });

            /**
             * Clear the item from the front of the queue that we've just done and start the next one if there
             * are any.
             */
            function processQueue() {
                refreshQueue.shift();
                if (refreshQueue.length) {
                    refreshFromServer();
                }
            }
        }

        /**
         * UserData available locally is out of date. Update it to the latest version.
         */
        function updateUserDataToLatestVersion() {
            self.refreshFromServer(function () {
                // Show a custom loader.
                Loader.suppressOnce();
                $('#userdata-loader').fadeIn(300);

                // Show the loader for some time so it doesn't "blink".
                setTimeout(function () {
                    Notify.success('Hotjar interface updated to the latest version.', {
                        when: 'onReload',
                        timeout: 6000
                    });
                    window.location.reload();
                }, 800);

                return $q.defer().promise; // No need to resolve since we're reloading the page.
            });
        }

        /**
         * Load all user data from LocalStorage, parse as JSON and return it.
         */
        function loadLocalUserData() {
            if (!getLoggedIn()) {
                self.clearUserData();
            }
            return loadUserDataFromStorage();
        }

        /*******************
         * INITIALIZATION
         *****************/
        userData = loadLocalUserData();
        self.set(userData);

        // Recollect & reload userData if we're on an old version of the data.
        if (self.loggedIn && userData.userId && userData.version !== window.settings.version) {
            updateUserDataToLatestVersion();
        }

        /************
         * LISTENERS
         ************/
        /**
         * Listen to siteLastDataRefreshed and update currentSite as needed.
         */
        $rootScope.$on('siteLastDataRefreshed', function (event, site) {
            if (site.id === userData.currentSite.id) {
                userData.currentSite.last_data = site.last_data;
                // HACK: We update manually the cache without refetching the endpoint after
                // It's bad but it's a temporary workaround until we have a proper endpoint for useCurrentSite
                const cachedUserResource = queryClient.getQueryData(USER_RESOURCES_QUERY_KEY);
                if (cachedUserResource) {
                    const cachedCurrentSite = cachedUserResource.sites.find(
                        (site) => site.id === userData.currentSite.id
                    );
                    if (cachedCurrentSite) {
                        cachedCurrentSite.lastData = site.last_data;
                        queryClient.setQueryData(USER_RESOURCES_QUERY_KEY, cachedUserResource);
                    }
                }
            }
        });

        bus.subscribe(trackingCodeInstalled, () => {
            self.refreshFromServer();
        });

        bus.subscribe(siteUrlUpdated, () => {
            self.refreshFromServer();
        });

        bus.subscribe(refreshData, ({ payload: callback }) => self.refreshFromServer(callback));

        bus.subscribe(currentSiteSet, ({ payload: { site } }) => {
            self.currentSite = mapSiteModelToDto(site);
        });

        bus.subscribe(currentSiteChanged, () => {
            // we load ls data with updated currentSite
            userData = loadLocalUserData();
            self.set(userData);
            // we invalidate all react queries
            queryClient.invalidateQueries();
            // we refresh data for userdata.js
            self.refreshFromServer();
        });
    });
